<template>
    <div 
        v-click-outside="closeDropdown" 
        class="btn-group" 
        :class="[disabled ? 'dropdown--disabled' : '']" 
        v-bind="$attrs" 
    >
        <BaseButton 
            id="dropdownMenuClickableInside" 
            class="btn--filter" 
            variant="outline" 
            variant-style="secondary"
            aria-expanded="false" 
            is-content
            @click="toggleDropdown"
        >
            <template #content>
                <span class="flex align-items-center">
                    <IconSvg name="filter" fill="#637579" width="12px" height="12px" />
                    <span class="ml--5">Filter</span>
                    <CountBadge :value="filterActive.count" class="ml--5" />
                </span>
            </template>
        </BaseButton>

        <transition name="fade">
            <div 
                v-if="showDropdown" 
                class="dropdown-menu w-100 p--0 no-shadow filter__dropdown-menu mb--35"
                :class="[showDropdown ? 'show' : '']"
            >   
                <form class="overflow-y-scroll max-h-500" @submit.prevent="">
                    <div class="action--btns__wrap">
                        <button class="action--btns__clear action--btns" type="reset" @click="clearFilter">
                            Clear all
                        </button>
                        <BaseButton 
                            label="Run filter" 
                            :disabled="disabled" 
                            type="submit" 
                            @submit="handleFilter"
                        />
                    </div>
                
                    <div class="mb--15">
                        <div v-if="filter.name.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Name" />
                            <input id="name" v-model="filter.name.value" class="form-control form-control-sm filter-input" type="text">
                        </div>

                        <div v-if="filter.currency.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Currency" classes="ml--0" />
                            <CustomDropdown 
                                id="currencyDropdown" 
                                v-model="filter.currency.value" 
                                :options="initialCurrencyList"
                                list-type="currencies" 
                                index-label="currency" 
                                size="sm"
                                multiple
                                :data-layer-id="2"
                                :add-on="selectedCurrency.symbol"
                                class="dropdown-currency" 
                                btn-text-class="text--upper" 
                                @search="searchCurrencies" 
                                @select="selectCurrency" 
                            />
                        </div>

                        <div v-if="filter.industry.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Industry" />
                            <CustomDropdown 
                                id="currencyDropdown" 
                                v-model="filter.industry.value" 
                                :options="industryData"
                                list-type="industries" 
                                size="sm"
                                multiple
                                :data-layer-id="2"
                                class="dropdown-currency" 
                                btn-text-class="text--upper" 
                            />
                        </div>

                        <div v-if="filter.email.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Email" />
                            <input id="email" v-model="filter.email.value" class="form-control form-control-sm filter-input" type="email">
                        </div>

                        <div v-if="filter.status.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Status" />
                            <div>
                                <div v-for="status in statusList" :key="status" class="form-check form-check-inline">
                                    <input
                                        :id="`status-${status}`" v-model="filter.status.value" class="form-check-input" type="checkbox"
                                        :value="status"
                                    >
                                    <label class="form-check-label text--capitalize" :for="`status-${status}`">{{ status }}</label>
                                </div>
                            </div>
                        </div>
                    
                        <div v-if="filter.title.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Title" />
                            <input id="projectTitle" v-model="filter.title.value" class="form-control form-control-sm filter-input" type="text">
                        </div>

                        <div v-if="filter.fieldType.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Select a feature" />
                            <CustomDropdown 
                                v-model="filter.fieldType.value" 
                                :options="['clients', 'projects', 'invoices']" 
                                list-type="fields"
                                size="sm" 
                                list-item-class="text--capitalize"
                                btn-classes="text--capitalize"
                                :data-layer-id="4"
                                :searchable="false"
                                @select="v => filter.fieldType.value = v" 
                            />
                        </div>

                        <div v-if="filter.projectType.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Project type" />
                            <select v-model="filter.projectType.value" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                <option v-for="status in ['paid', 'not paid']" :key="status" :value="status" class="text--capitalize">
                                    {{ status }}
                                </option>
                            </select>
                        </div>

                        <div v-if="filter.priority.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Priority" />
                            <div>
                                <div v-for="priority in priorities" :key="priority" class="form-check form-check-inline">
                                    <input
                                        id="inlineCheckbox1" v-model="filter.priority.value" class="form-check-input" type="checkbox"
                                        :value="priority"
                                    >
                                    <label class="form-check-label text--capitalize" for="inlineCheckbox1">{{ priority }}</label>
                                </div>
                            </div>
                        </div>

                        <div v-if="filter.rateType.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Rate card type" />
                            <div>
                                <div v-for="item in rateTypes" :key="item" class="form-check form-check-inline">
                                    <input
                                        :id="`check-${item}`" v-model="filter.rateType.value" :value="item" class="form-check-input"
                                        type="checkbox"
                                    >
                                    <label class="form-check-label text--capitalize" :for="`check-${item}`">{{ item }}</label>
                                </div>
                            </div>
                        </div>

                        <div v-if="filter.invoiceNo.allowedPaths.includes(pageType)" class="filter-item-input">
                            <FormLabel title="Invoice No." />
                            <input id="invoiceNo" v-model="filter.invoiceNo.value" class="form-control form-control-sm" type="text">
                        </div>
                    </div>
                </form>
            </div>
        </transition>
    </div>
</template>

<script>
import { 
    projectFilterStatuses, 
    priorities, 
    taskStatus, 
    invoiceFilterStatuses, 
    clientFilterStatuses, 
} from "@/utils/enums";
import { industryData, rateTypes } from "@/utils/dummy";
import { serilaizeQuery } from "@/utils/helpers";
import FormLabel from "../forms/FormLabel.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import BaseButton from "../buttons/BaseButton.vue";
import DropDownWithSub from "../dropdowns/DropDownWithSub.vue";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import CountBadge from "../badges/CountBadge.vue";

export default {
    name: "MainFilterTwo",

    components: {
        FormLabel,
        IconSvg, 
        BaseButton,
        CustomDropdown,
        CountBadge,
    },

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        pageType: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            query: {},
            projectFilterStatuses,
            priorities,
            taskStatus,
            invoiceFilterStatuses,
            clientFilterStatuses,
            industryData,
            currentPathName: this.$route.path || "",
            filter: this.getInitialFilterValues(),
            rateTypes,
            initialCurrencyList: Object.values(this.$currencies_obj),
            selectedCurrency: {},
            currencyMap: this.$supported_currencies_map,
            showDropdown: false,
            dropdownPosition: "",
            dropdownStyles: {},
        };
    },

    computed: {
        statusList() {
            switch (this.pageType) {
            case "projects":
                return this.projectFilterStatuses;
                
            case "invoices":
                return this.invoiceFilterStatuses;
                    
            case "clients":
                return this.clientFilterStatuses;
                    
            case "tasks":
                return this.taskStatus;
                
            default:
                return [];
            }
        },

        filterActive() {
            const values = Object.values(this.filter);
            let isActive = true;
            let count = 0;

            if (values.length) {
                for (let val of values) {                    
                    if (val?.value && val?.value !== null) {                        
                        if (Array.isArray(val.value) && val.value.length) {                            
                            isActive = true;
                            count++;
                        } 
                        
                        if (typeof val.value === "string") {
                            isActive = true;
                            count++;
                        }
                    } 
                }
            }

            return {
                isActive,
                count,
            };
        },
    },

    watch: {
        showDropdown(newVal) {            
            if (newVal) {
                // Calculate position for nested dropdowns
                this.$nextTick(() => {
                    this.calculateDropdownPosition();
                });
            }
        },
    },

    mounted() {    
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("scroll", this.handleScroll);
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("scroll", this.handleScroll);
    },

    methods: {
        getInitialFilterValues() {            
            const filterData = {
                rateType: { allowedPaths: ["rateCards"], value: this.$route.query.rateType?.split(",") || [] },
                name: { allowedPaths: ["clients", "tags", "tasks"], value: this.$route.query.name || "" },
                industry: { allowedPaths: ["clients"], value: this.$route.query.industry || "" },
                priority: { allowedPaths: ["projects", "tasks"], value: this.$route.query.priority?.split(",") || [] },
                email: {
                    allowedPaths: ["clients", "invoices", "meetings"],
                    value: this.$route.query.email || "",
                },
                title: { 
                    allowedPaths: ["projects", "rateCards", "meetings"], value: this.$route.query.title || "", 
                },
                status: { allowedPaths: ["projects", "clients", "invoices", "meetings"], value: this.$route.query?.status?.split(",") || [] },
                invoiceNo: { allowedPaths: ["invoices"], value: this.$route.query.invoiceNo || "" },
                currency: { 
                    allowedPaths: ["invoices", "rateCards", "revenue"], 
                    value: this.$route.query.currency || "", 
                },
                projectType: { allowedPaths: ["projects"], value: this.$route.query.projectType || "" },
                fieldType: { allowedPaths: ["reports"], value: this.$route.query.fieldType || "" },
            };

            if (this.pageType === "reports") {
                filterData.fieldType.value = "clients";
            }

            return filterData;
        },

        clearFilter() {
            const currentRoute = this.$route.path;
            const queryParam = serilaizeQuery({});
            
            this.$router.replace(`${currentRoute}?${queryParam}`);
        },

        handleFilter() {
            const query = this.query;

            if (this.$route.query.page){
                delete query.page;
            }

            if (this.$route.query.limit){
                delete query.limit;
            }

            if (this.filter.name.value) {
                query.name = this.filter.name.value;
            }

            if (this.filter.title.value) {
                query.title = this.filter.title.value;
            }

            if (this.filter.industry.value) {
                query.industry = this.filter.industry.value;
            }

            if (this.filter.email.value) {
                query.email = this.filter.email.value;
            }

            if (this.filter.status.value.length) {
                query.status = this.filter.status.value;
            }

            if (this.filter.currency.value) {
                query.currency = this.filter.currency.value;
            }

            if (this.filter.invoiceNo.value) {
                query.invoiceNo = this.filter.invoiceNo.value;
            }

            if (this.filter.priority.value.length) {
                query.priority = this.filter.priority.value;
            }

            if (this.filter.rateType.value.length) {
                query.rateType = this.filter.rateType.value;
            }

            if (this.filter.projectType.value) {
                query.projectType = this.filter.projectType.value;
            }

            if (this.filter.fieldType.value) {
                query.fieldType = this.filter.fieldType.value;
            }

            const currentRoute = this.$route.path;
            const queryParam = serilaizeQuery(query);
                        
            this.$router.replace(`${currentRoute}?${queryParam}`);
        },

        selectStatus(val) {
            const item = this.filter.status.value.find(x => x?._id === val?._id);

            if (!item) {
                filter.status.value = [ ...filter.status.value, val];
            };
        },

        removeStatusItem(item) {
            filter.status.value = filter.status.value.filter(x => x?._id !== item?._id);
        },

        searchCurrencies(val) {
            const initialList = this.initialCurrencyList;

            if (val && typeof val === "string") {
                const filteredList = initialList.filter(item => item.toLowerCase().includes(val.toLowerCase()));
                this.initialCurrencyList = filteredList;
            } else {
                this.initialCurrencyList = Object.values(this.$currencies_obj);
            }
        },

        selectCurrency(item) {
            const index = item && item.toLowerCase() || "";

            this.selectedCurrency = this.currencyMap[index];
            this.filter.currency.value = item;
        },

        handleResize() {
            if (this.showDropdown) {
                this.calculateDropdownPosition();
            }
        },
    
        handleScroll() {
            if (this.showDropdown && this.dataLayerId > 1) {
                this.calculateDropdownPosition();
            }
        },

        toggleDropdown() {
            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                this.$nextTick(() => {
                    this.calculateDropdownPosition();
                });
            }
        },

        calculateDropdownPosition() {
            if (!this.$refs.dropdownMenu) return;
            
            const dropdown = this.$el;
            const menu = this.$refs.dropdownMenu;
            const rect = dropdown.getBoundingClientRect();
            const menuRect = menu.getBoundingClientRect();
            
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            
            // Calculate if dropdown should open upward
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = menuRect.height || 300; // fallback height
            
            // Calculate if dropdown should open to the left
            const spaceRight = viewportWidth - rect.left;
            const spaceLeft = rect.right;
            const dropdownWidth = menuRect.width || 200; // fallback width
            
            let positionClass = "";
            let styles = {
                zIndex: this.dynamicZIndex,
            };
            
            // Vertical positioning
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                positionClass += " dropup";
            }
            
            // Horizontal positioning
            if (spaceRight < dropdownWidth && spaceLeft > dropdownWidth) {
                positionClass += " dropdown-right";
            }
            
            // For nested dropdowns, use fixed positioning
            if (this.dataLayerId > 1) {
                styles.position = "fixed";
                styles.top = `${rect.bottom}px`;
                styles.left = `${rect.left}px`;
                styles.minWidth = `${rect.width}px`;
                
                // Adjust if would go off-screen
                if (rect.bottom + dropdownHeight > viewportHeight) {
                    styles.top = `${rect.top - dropdownHeight}px`;
                }
                
                if (rect.left + dropdownWidth > viewportWidth) {
                    styles.left = `${rect.right - dropdownWidth}px`;
                }
            }
        
            this.dropdownPosition = positionClass;
            this.dropdownStyles = styles;
        },

        getUniqueId(item) {
            return item._id || item[this.indexLabel] || item;
        },

        closeDropdown() {
            this.showDropdown = false;
            this.dropdownPosition = "";
            this.dropdownStyles = {};
        },
    },
};
</script>

<style lang="scss" scoped>
    .action--btns__wrap {
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
        padding-top: 10px;
        background-color: #f6f8fa !important;
        width: 100%;
        position: sticky;
        top: 0;
        z-index: 10;
    }

    .dropdown-menu.show {
        transform: translate3d(-10px, 30px, 0px) !important;
        transform: translate3d(0px, 30px, 0px) !important;
        width: 300px !important;
        inset: 0px 0px auto auto !important;
        padding: 0px !important;
        box-shadow: inset 0 -1px rgba(235, 238, 241, 0) !important; 
        border-radius: 5px;
   }

   .filter {
        &-input {
            font-size: 12px !important;
            border-radius: 6px;
        }

        &-item-input {
            display: flex;
            flex-direction: column;
            padding: 10px 11px;

            &:not(:last-of-type) {
                border-bottom: 1px solid #eee;
            }

            &:last-of-type {
                padding-bottom: 0px;
            }

            label {
                font-size: 12px !important;
            }
        }

        &-status {
            &-item {
                flex-direction: row !important;
                flex-wrap: wrap;

                label {
                    margin-bottom: 0px !important;
                }
            }
        }
   }
</style>